import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useBedarf, useUpdateBedarf } from '../../api/bedarfe';
import { BedarfStatusBadge, BedarfPriorityBadge, MatchingBewerberSection, BedarfStatusActionButtons, SchulbegleiterCard } from '../../components/bedarfe';
import { BedarfActionDropdown } from '../../components/bedarfe/actions';
import { BedarfStatus, GenderConfig, formatHours, calculateTotalHours } from '../../types/bedarf';
import { OnboardingChecklist } from '../../components/bedarfe/OnboardingChecklist';
import TagesArbeitszeit from '../../components/bedarfe/TagesArbeitszeit';
import { ActivityTimeline } from '../../components/activity';
import { UserSelector } from '../../components/UserSelector';
import { formatAddressLines } from '../../utils/formatAddress';

export function BedarfDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useBedarf(id!);
  const updateMutation = useUpdateBedarf();

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-500">Laden...</p>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Bedarf nicht gefunden</p>
        <Link to="/bedarfe" className="mt-4 inline-block text-primary-600 hover:text-primary-500">
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  const bedarf = data.data;
  const childAddressLines = formatAddressLines(
    bedarf.childStreet,
    bedarf.childPostalCode,
    bedarf.childCity?.name,
  );
  const schoolAddressLines = formatAddressLines(
    bedarf.schoolStreet,
    bedarf.schoolPostalCode,
    bedarf.schoolCity?.name,
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <button
          onClick={() => {
            const listUrl = sessionStorage.getItem('bedarfe-list-url');
            navigate(listUrl || '/bedarfe');
          }}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center mb-3"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Zurück zur Liste
        </button>

        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {bedarf.childFirstName}{bedarf.childLastName ? ` ${bedarf.childLastName}` : ''}
            </h1>
            <div className="mt-2 flex items-center space-x-4">
              <BedarfStatusBadge status={bedarf.status} />
              <BedarfPriorityBadge priority={bedarf.priority} />
            </div>
          </div>
          <div className="mt-3 sm:mt-0 flex items-center space-x-2">
            <UserSelector
              id="assignedTo"
              value={bedarf.assignedToId}
              onChange={async (userId) => {
                try {
                  await updateMutation.mutateAsync({
                    id: bedarf.id,
                    data: { assignedToId: userId },
                  });
                  toast.success(userId ? 'Zuweisung aktualisiert' : 'Zuweisung entfernt');
                } catch {
                  toast.error('Fehler beim Aktualisieren der Zuweisung');
                }
              }}
              disabled={updateMutation.isPending}
              className="w-44"
            />
            <Link
              to={`/bedarfe/${bedarf.id}/edit`}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Bearbeiten
            </Link>
            <BedarfActionDropdown bedarf={bedarf} includePrimaryActions={false} hideActionKeys={['edit']} />
          </div>
        </div>

        <BedarfStatusActionButtons bedarf={bedarf} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Onboarding checklist (only during onboarding / after winning) */}
          {(bedarf.status === BedarfStatus.ONBOARDING || bedarf.status === BedarfStatus.GEWONNEN) && (
            <OnboardingChecklist bedarf={bedarf} />
          )}

          {/* Stammdaten */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Stammdaten des Kindes</h3>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{bedarf.childFirstName}{bedarf.childLastName ? ` ${bedarf.childLastName}` : ''}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Geburtsdatum</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {bedarf.childDateOfBirth ? new Date(bedarf.childDateOfBirth).toLocaleDateString('de-DE') : '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Geschlecht</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {bedarf.childGender ? GenderConfig[bedarf.childGender].label : '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Adresse</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {childAddressLines.length > 0
                      ? childAddressLines.map((line) => <div key={line}>{line}</div>)
                      : '-'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Schulische Angaben */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Schulische Angaben</h3>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Schule</dt>
                  <dd className="mt-1 text-sm text-gray-900">{bedarf.schoolName || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Klasse</dt>
                  <dd className="mt-1 text-sm text-gray-900">{bedarf.schoolClass || '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Adresse</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {schoolAddressLines.length > 0
                      ? schoolAddressLines.map((line) => <div key={line}>{line}</div>)
                      : '-'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Bedarf & Diagnose */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Bedarf & Diagnose</h3>
            </div>
            <div className="px-4 py-5 sm:p-6 space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Diagnose</dt>
                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{bedarf.diagnosis || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Unterstützungsbedarf</dt>
                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{bedarf.supportDescription || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Besondere Anforderungen</dt>
                <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{bedarf.specialNeeds || '-'}</dd>
              </div>
            </div>
          </div>

          {/* Stundenplan */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Stundenplan</h3>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <div className="grid grid-cols-5 gap-4 text-center mb-4">
                <div>
                  <div className="text-xs font-medium text-gray-500">Mo</div>
                  <div className="mt-1 text-sm text-gray-900">
                    {bedarf.startTimeMonday && bedarf.endTimeMonday
                      ? `${bedarf.startTimeMonday} - ${bedarf.endTimeMonday}`
                      : '-'}
                  </div>
                  <TagesArbeitszeit startTime={bedarf.startTimeMonday} endTime={bedarf.endTimeMonday} />
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">Di</div>
                  <div className="mt-1 text-sm text-gray-900">
                    {bedarf.startTimeTuesday && bedarf.endTimeTuesday
                      ? `${bedarf.startTimeTuesday} - ${bedarf.endTimeTuesday}`
                      : '-'}
                  </div>
                  <TagesArbeitszeit startTime={bedarf.startTimeTuesday} endTime={bedarf.endTimeTuesday} />
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">Mi</div>
                  <div className="mt-1 text-sm text-gray-900">
                    {bedarf.startTimeWednesday && bedarf.endTimeWednesday
                      ? `${bedarf.startTimeWednesday} - ${bedarf.endTimeWednesday}`
                      : '-'}
                  </div>
                  <TagesArbeitszeit startTime={bedarf.startTimeWednesday} endTime={bedarf.endTimeWednesday} />
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">Do</div>
                  <div className="mt-1 text-sm text-gray-900">
                    {bedarf.startTimeThursday && bedarf.endTimeThursday
                      ? `${bedarf.startTimeThursday} - ${bedarf.endTimeThursday}`
                      : '-'}
                  </div>
                  <TagesArbeitszeit startTime={bedarf.startTimeThursday} endTime={bedarf.endTimeThursday} />
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">Fr</div>
                  <div className="mt-1 text-sm text-gray-900">
                    {bedarf.startTimeFriday && bedarf.endTimeFriday
                      ? `${bedarf.startTimeFriday} - ${bedarf.endTimeFriday}`
                      : '-'}
                  </div>
                  <TagesArbeitszeit startTime={bedarf.startTimeFriday} endTime={bedarf.endTimeFriday} />
                </div>
              </div>
              <div className="border-t pt-4 flex justify-between items-center">
                <span className="text-sm text-gray-500">Gesamt pro Woche</span>
                <span className="text-lg font-bold text-primary-600">
                  {calculateTotalHours(bedarf) > 0 ? formatHours(calculateTotalHours(bedarf)) : '-'}
                </span>
              </div>
              {bedarf.includesBreaksOgs && (
                <div className="mt-2 text-sm text-gray-500">
                  inkl. Pausen/OGS
                </div>
              )}
            </div>
          </div>

          {/* Notizen */}
          {bedarf.notes && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Notizen</h3>
              </div>
              <div className="px-4 py-5 sm:p-6">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{bedarf.notes}</p>
              </div>
            </div>
          )}

          {/* Matching Bewerber */}
          <MatchingBewerberSection
            bedarfId={bedarf.id}
            childName={bedarf.childLastName ? `${bedarf.childFirstName} ${bedarf.childLastName}` : bedarf.childFirstName}
            schoolCity={bedarf.schoolCity?.name || null}
            schoolName={bedarf.schoolName}
          />

          {/* Activity Timeline */}
          <ActivityTimeline bedarfId={bedarf.id} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Wer auf dieser Stelle steht — die erste Frage, nicht die letzte. */}
          <SchulbegleiterCard bedarf={bedarf} />

          {/* Rahmenbedingungen */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Rahmenbedingungen</h3>
            </div>
            <div className="px-4 py-5 sm:p-6 space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Gewünschter Beginn</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {bedarf.startDateRequested ? new Date(bedarf.startDateRequested).toLocaleDateString('de-DE') : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Voraussichtliches Ende</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {bedarf.endDateRequested ? new Date(bedarf.endDateRequested).toLocaleDateString('de-DE') : '-'}
                </dd>
              </div>
              {bedarf.currentSituation && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Aktuelle Situation</dt>
                  <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{bedarf.currentSituation}</dd>
                </div>
              )}
            </div>
          </div>

          {/* Kostenträger */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Kostenträger / Formalien</h3>
            </div>
            <div className="px-4 py-5 sm:p-6 space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Kostenträger</dt>
                <dd className="mt-1 text-sm text-gray-900">{bedarf.kostentraegerName || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Ansprechpartner</dt>
                <dd className="mt-1 text-sm text-gray-900">{bedarf.kostentraegerAnsprechpartner || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">E-Mail</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {bedarf.kostentraegerEmail ? (
                    <a href={`mailto:${bedarf.kostentraegerEmail}`} className="text-primary-600 hover:text-primary-500">
                      {bedarf.kostentraegerEmail}
                    </a>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Telefon</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {bedarf.kostentraegerPhone ? (
                    <a href={`tel:${bedarf.kostentraegerPhone}`} className="text-primary-600 hover:text-primary-500">
                      {bedarf.kostentraegerPhone}
                    </a>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Rechtsgrundlage</dt>
                <dd className="mt-1 text-sm text-gray-900">{bedarf.legalBasis || '-'}</dd>
              </div>
              <div className="flex items-center space-x-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bedarf.hasApproval ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {bedarf.hasApproval ? 'Bewilligung vorhanden' : 'Keine Bewilligung'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bedarf.hasCarePlan ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {bedarf.hasCarePlan ? 'Hilfeplan vorhanden' : 'Kein Hilfeplan'}
                </span>
              </div>
            </div>
          </div>

          {/* Elternkontakt */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Elternkontakt</h3>
            </div>
            <div className="px-4 py-5 sm:p-6 space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{bedarf.parentName || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Telefon</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {bedarf.parentPhone ? (
                    <a href={`tel:${bedarf.parentPhone}`} className="text-primary-600 hover:text-primary-500">
                      {bedarf.parentPhone}
                    </a>
                  ) : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">E-Mail</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {bedarf.parentEmail ? (
                    <a href={`mailto:${bedarf.parentEmail}`} className="text-primary-600 hover:text-primary-500">
                      {bedarf.parentEmail}
                    </a>
                  ) : '-'}
                </dd>
              </div>
            </div>
          </div>

          {/* Meta Info */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Informationen</h3>
            </div>
            <div className="px-4 py-5 sm:p-6 space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Erstellt am</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(bedarf.createdAt).toLocaleDateString('de-DE')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Erstellt von</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {bedarf.createdBy ? `${bedarf.createdBy.firstName} ${bedarf.createdBy.lastName}` : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Zuletzt geändert</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(bedarf.updatedAt).toLocaleDateString('de-DE')}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
